import React from 'react';
import { Drawer, Badge } from '@biq/apex-ui';
import { Box, Typography } from '@mui/material';
import { HelixLoader } from '../../../../components';
import { useDictionaryList } from '../../../../hooks';
import { getNodeColor } from '../lifegraph-explorer/lifegraph-explorer-helper';
import GeneralDetail from './components/GeneralDetail';
import { LinkDetails } from './components/LinkDetails';
import {
    NodeDetailDrawerProps,
    anchorPoint,
    nodeDetailDrawerStyles,
    LinkDetailsProps,
} from './node-detail-drawer-helper';

export const NodeDetailDrawer = ({
    isOpen,
    setDrawerOpen,
    data,
    nodeColors,
}: NodeDetailDrawerProps): React.JSX.Element => {
    const { data: dictionaryList, isLoading: areDictionaryLoading } =
        useDictionaryList();
    const isNodeData = !data?.edge;
    const selectedNodeColor = getNodeColor(data, nodeColors);
    const displayNodeColor = isNodeData && selectedNodeColor;
    const dictionaryName = data?.sdo?.header?.dictionary ?? 'N/A';
    const headerId = data?.sdo?.header?.id ?? 'N/A';
    const title = isNodeData ? dictionaryName : data?.label;
    // Only display possible id for nodes
    const subheader = isNodeData ? `ID: ${headerId}` : '';

    const toggleDrawer =
        (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
            if (
                event.type === 'keydown' &&
                ((event as React.KeyboardEvent).key === 'Tab' ||
                    (event as React.KeyboardEvent).key === 'Shift')
            ) {
                return;
            }
            setDrawerOpen(open);
        };

    const TitleElement: React.JSX.Element = (
        <Box sx={nodeDetailDrawerStyles.titleContainer}>
            <Typography style={nodeDetailDrawerStyles.titleStyle}>
                {title}
            </Typography>
            <Box sx={nodeDetailDrawerStyles.nodeColorContainer}>
                <Badge
                    sx={{
                        ...nodeDetailDrawerStyles.nodeColor,
                        backgroundColor: selectedNodeColor,
                    }}
                />
            </Box>
        </Box>
    );

    const getDetailsDrawerContent = (): React.JSX.Element => {
        if (areDictionaryLoading) {
            return <HelixLoader />;
        } else if (isNodeData) {
            const dictionaries = dictionaryList?.data ?? [];
            return (
                <GeneralDetail
                    data={data}
                    dictionaries={dictionaries}
                    dictionaryName={title}
                />
            );
        } else {
            return <LinkDetails data={data?.edge as LinkDetailsProps} />;
        }
    };

    return (
        <Drawer
            closable
            open={isOpen}
            title={title}
            subheader={subheader}
            drawerType={'rounded'}
            anchor={anchorPoint}
            sx={nodeDetailDrawerStyles.detailsDrawer}
            onClose={toggleDrawer(false)}
            titleChildren={displayNodeColor ? TitleElement : null}
            subheaderStyles={nodeDetailDrawerStyles.subheaderStyle}
        >
            {getDetailsDrawerContent()}
        </Drawer>
    );
};
